import type { CSSProperties, ReactNode } from 'react'
import { NIGHT, PAPER, RADIUS_CARD } from '../tokens'

interface TornCardProps {
  /** Content on the flat night stage, above the tear (headline, mascot, doodles). */
  stage: ReactNode
  /** Content on the paper panel, below the tear (cards, chat bubbles, etc). */
  children: ReactNode
  /** Positioned absolutely at the tear line itself -- see Doodle's `spilling` variant. */
  spill?: ReactNode
  style?: CSSProperties
}

// Same jagged edge as the approved mockup -- kept as one constant so every
// TornCard in the product tears the same way.
const TEAR_CLIP_PATH =
  'polygon(0% 6%, 3% 2%, 7% 5%, 12% 1%, 17% 4%, 23% 0%, 29% 3%, 35% 1%, 41% 5%, 47% 2%, 53% 4%, 59% 1%, 65% 3%, 71% 0%, 77% 4%, 83% 1%, 89% 3%, 95% 0%, 100% 4%, 100% 100%, 0% 100%)'

/**
 * The torn-paper reveal -- Direction F's one genuinely new structural device.
 * "Tear open the page to see what's inside" is a literal fit for Understand
 * Something tearing into a chapter, and it doubles as a section-break for
 * any other reveal moment (a badge unlocking, a test result opening).
 */
export function TornCard({ stage, children, spill, style }: TornCardProps) {
  return (
    <div
      style={{
        background: NIGHT,
        borderRadius: RADIUS_CARD,
        padding: '3rem 3rem 0',
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>{stage}</div>
      <div
        style={{
          background: PAPER,
          borderRadius: `${RADIUS_CARD}px ${RADIUS_CARD}px 0 0`,
          marginTop: '2.4rem',
          padding: '2.4rem 2.6rem 2rem',
          position: 'relative',
          clipPath: TEAR_CLIP_PATH,
        }}
      >
        {spill && (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: '100%', height: 0, zIndex: 2 }}>
            {spill}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}
